/**
 * Iter-291 — Event QR Code Manager
 *
 * Used by admin & client to:
 *   • Toggle attendance methods (QR / GPS / Selfie) for a shift
 *   • Generate a SHIFT-level QR code (one PNG per shift/day)
 *   • Generate an EVENT-level QR code (one PNG that covers every shift in
 *     the series — useful for simple single-day events that don't need a
 *     different code per day)
 *   • Print or download the chosen QR PNG
 *
 * Route: /shifts/:shiftId/qr  (registered in App.js)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Loader2, ArrowLeft, QrCode, Printer, Download, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function EventQRManager() {
  const { shiftId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [methods, setMethods] = useState(null);          // {require_qr_checkin, require_gps_checkin, require_selfie_checkin, series_id}
  const [shiftToken, setShiftToken] = useState(null);    // shift-level QR token
  const [eventToken, setEventToken] = useState(null);    // event-level QR token
  const [activeScope, setActiveScope] = useState('shift');// which PNG to render
  const [generating, setGenerating] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const m = await api.get(`/attendance/methods/${shiftId}`);
      setMethods(m.data);
      // Probe overview for an existing shift QR token (legacy endpoint
      // returns `qr_code_token`).
      try {
        const ov = await api.get(`/attendance/overview/${shiftId}`);
        if (ov.data?.qr_code_token) setShiftToken(ov.data.qr_code_token);
      } catch (_) { /* nothing yet */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load shift attendance config');
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const updateMethod = async (field, value) => {
    const next = { ...methods, [field]: value };
    setMethods(next);
    try {
      await api.patch(`/attendance/methods/${shiftId}`, { [field]: value });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
      setMethods(methods);  // revert
    }
  };

  const generateShiftQR = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/attendance/qr/generate/${shiftId}`);
      setShiftToken(r.data.qr_code_token);
      setActiveScope('shift');
      toast.success('Shift QR ready');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const generateEventQR = async () => {
    if (!methods?.series_id) {
      toast.error('This shift is not part of an event series.');
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post(`/attendance/qr/event/generate`, {
        series_id: methods.series_id,
        label: null,
      });
      setEventToken(r.data.qr_code_token);
      setActiveScope('event');
      toast.success('Event QR ready');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const renderedToken = activeScope === 'event' ? eventToken : shiftToken;
  const imageUrl = renderedToken
    ? (activeScope === 'event'
        ? `${API}/api/attendance/qr/event/image/${renderedToken}`
        : `${API}/api/attendance/qr/image/${renderedToken}`)
    : null;

  const handlePrint = () => {
    if (!imageUrl) return;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>QR · iEvntür</title>
      <style>body{font-family:system-ui;text-align:center;padding:40px}
      img{width:480px;height:480px;max-width:90vw}
      h1{font-size:22px;margin-bottom:6px}
      .label{font-size:13px;color:#475569;margin-top:14px}
      .scope{display:inline-block;background:#ecfeff;color:#0e7490;
        font-size:11px;letter-spacing:.06em;text-transform:uppercase;
        padding:4px 10px;border-radius:999px;margin-bottom:10px}
      </style></head><body>
        <div class="scope">${activeScope === 'event' ? 'Event QR' : 'Shift QR'}</div>
        <h1>Scan to clock in / clock out</h1>
        <p class="label">${renderedToken}</p>
        <img src="${imageUrl}" alt="QR" />
        <p class="label">Powered by iEvntür</p>
        <script>window.onload = () => window.print();</script>
      </body></html>`);
    w.document.close();
  };

  const handleDownload = async () => {
    if (!imageUrl) return;
    const r = await fetch(imageUrl);
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `ievntur-qr-${activeScope}-${renderedToken}.png`;
    a.click();
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="event-qr-manager">
      <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-5">
        <button onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900" data-testid="qr-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <QrCode className="w-5 h-5 text-cyan-600" /> Attendance methods
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Choose any combination of methods. Talents must satisfy every method you turn ON.
            </p>
            <MethodRow
              label="QR Code"
              hint="Talent scans a printed/displayed QR to clock in/out."
              checked={!!methods.require_qr_checkin}
              onChange={(v) => updateMethod('require_qr_checkin', v)}
              testid="method-qr"
            />
            <MethodRow
              label="GPS"
              hint="Talent's location must be within the venue radius."
              checked={!!methods.require_gps_checkin}
              onChange={(v) => updateMethod('require_gps_checkin', v)}
              testid="method-gps"
            />
            <MethodRow
              label="Selfie"
              hint="Talent must capture a selfie at clock in/out."
              checked={!!methods.require_selfie_checkin}
              onChange={(v) => updateMethod('require_selfie_checkin', v)}
              testid="method-selfie"
            />
            {!methods.require_qr_checkin && !methods.require_gps_checkin && !methods.require_selfie_checkin && (
              <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5" data-testid="qr-no-method-warning">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
                With every method OFF, talents can clock in/out without verification.
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-xl">QR codes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                variant={activeScope === 'shift' ? 'default' : 'outline'}
                disabled={generating}
                onClick={() => (shiftToken ? setActiveScope('shift') : generateShiftQR())}
                data-testid="qr-shift-btn"
              >
                {shiftToken ? 'Show shift QR' : 'Generate shift QR'}
              </Button>
              <Button
                variant={activeScope === 'event' ? 'default' : 'outline'}
                disabled={generating || !methods.series_id}
                onClick={() => (eventToken ? setActiveScope('event') : generateEventQR())}
                title={methods.series_id ? 'One QR for the whole event' : 'Single-shift event — no series'}
                data-testid="qr-event-btn"
              >
                {eventToken ? 'Show event QR' : 'Generate event QR'}
              </Button>
            </div>

            {!methods.series_id && (
              <p className="text-xs text-slate-500" data-testid="qr-no-series-hint">
                This shift is standalone (no series). Use the shift QR — it
                covers this exact date/time.
              </p>
            )}

            {renderedToken ? (
              <div className="rounded-lg border border-slate-200 bg-white p-4 flex flex-col items-center text-center" data-testid="qr-preview">
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full mb-2 ${activeScope === 'event' ? 'bg-violet-100 text-violet-700' : 'bg-cyan-100 text-cyan-700'}`}>
                  {activeScope === 'event' ? 'Event-wide QR' : 'Shift QR'}
                </span>
                <img
                  src={imageUrl}
                  alt="QR code"
                  className="w-64 h-64 sm:w-72 sm:h-72 object-contain"
                  data-testid="qr-image"
                />
                <p className="text-xs font-mono text-slate-500 mt-2">{renderedToken}</p>
                <div className="flex flex-wrap gap-2 mt-4 justify-center">
                  <Button variant="outline" size="sm" onClick={handlePrint} data-testid="qr-print">
                    <Printer className="w-4 h-4 mr-1.5" /> Print
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDownload} data-testid="qr-download">
                    <Download className="w-4 h-4 mr-1.5" /> Download PNG
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => activeScope === 'event' ? generateEventQR() : generateShiftQR()}
                    data-testid="qr-refresh"
                  >
                    <RefreshCw className="w-4 h-4 mr-1.5" /> Re-generate
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500" data-testid="qr-empty">
                Generate a QR code above. You can keep both a shift-level and an event-level code.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function MethodRow({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <div>
        <Label className="text-sm font-medium text-slate-800">{label}</Label>
        <p className="text-xs text-slate-500 mt-0.5">{hint}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
